/* eslint-disable @typescript-eslint/strict-boolean-expressions */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
import axios, { AxiosResponse } from 'axios';
import httpService from './index';
import { NOTIFICATION_TYPES } from '../contexts/NotificationContext';
import { ApplicationBranding, ApplicationBrandingLastUpdated } from './types/ApplicationBranding';
import logger from '../utils/logger';

// Application Branding endpoints - moved back to public services
// These are public-facing content (banners, page titles) needed before authentication
const getApplicationBrandings = async (hostName: string): Promise<AxiosResponse<ApplicationBranding[]>> => {
  logger.api.debug('Fetching application brandings for:', hostName);
  try {
    const response = await httpService.http.get(
      `/${process.env.REACT_APP_MSC_PATH}/app_branding_objects/list?msc_base_url=${hostName}`
    );
    logger.api.debug('Application brandings fetched successfully');
    return response;
  } catch (error) {
    logger.api.error('Error fetching application brandings:', error);
    throw error;
  }
};

const getApplicationBrandingTimestamp = async (hostName: string): Promise<AxiosResponse<ApplicationBrandingLastUpdated>> => {
  logger.api.debug('Fetching application branding timestamp for:', hostName);
  try {
    const response = await httpService.http.get(
      `/${process.env.REACT_APP_MSC_PATH}/app_branding_objects/updated/timestamp?msc_base_url=${hostName}`
    );
    logger.api.debug('Application branding timestamp fetched successfully');
    return response;
  } catch (error) {
    logger.api.error('Error fetching application branding timestamp:', error);
    throw error;
  }
};

const getCountriesDscUm = async (): Promise<AxiosResponse<any, any>> =>
  await httpService.get(`/${process.env.REACT_APP_MSC_PATH}/countries/list`);

const getCountries = async (): Promise<AxiosResponse> => {
  logger.api.debug('Fetching countries from /rafadmin/country');
  try {
    const response = await httpService.get(`/${process.env.REACT_APP_MSC_PATH}/rafadmin/country`);
    logger.api.debug('Countries fetched successfully:', response.data?.length, 'items');
    return response;
  } catch (error) {
    logger.api.error('Error fetching countries:', error);
    throw error;
  }
};

const getLanguages = async (): Promise<AxiosResponse> => {
  logger.api.debug('Fetching languages from /rafadmin/language');
  try {
    const response = await httpService.get(`/${process.env.REACT_APP_MSC_PATH}/rafadmin/language`);
    logger.api.debug('Languages fetched successfully:', response.data?.length, 'items');
    return response;
  } catch (error) {
    logger.api.error('Error fetching languages:', error);
    throw error;
  }
};

const getCurrentCountry = async (): Promise<AxiosResponse<any, any>> => await axios.get('https://ipapi.co/json/');

// MOVED TO protectedServices.ts - these endpoints now require authentication:
// - getCustomers (customers/list)
// - getCoList (customer_objects) 
// - getDscUmUsersList (dsc-users)
// - exportDscUmUsers (dsc-users/export)
// - getBusinessFunctions (business_functions)

const activateUser = async (token: string): Promise<AxiosResponse<any, any>> =>
  await httpService.get(`/${process.env.REACT_APP_MSC_PATH}/api/admin/activateUser?token=${token}`);

const getBrandingObjects2 = async (queryParams: string): Promise<AxiosResponse<any, any>> =>
  await httpService.get(`/${process.env.REACT_APP_MSC_PATH}/branding_objects/list?${queryParams}`);

const getRegion = async (): Promise<AxiosResponse<any[], any>> =>
  await httpService.get(`/${process.env.REACT_APP_MSC_PATH}/regions/list`);

export const getRegionsDscUm = async (): Promise<AxiosResponse<any, any>> =>
  await httpService.get(`/${process.env.REACT_APP_MSC_PATH}/regions/list`);

// Business units endpoint moved to protectedServices.ts
// This now requires authentication to access

const getAgs = async (businessUnit?: string): Promise<AxiosResponse<any, any>> => {
  const url = businessUnit ? `?businessUnit=${businessUnit}` : '';
  return await httpService.get(`/${process.env.REACT_APP_MSC_PATH}/ags/list${url}`);
};

export interface RequestContext {
  inputData: any;
  request: (inputData: any) => Promise<AxiosResponse<any, any>>;
  prefetch?: () => void;
  postfetch?: () => void;
  setLoading: (data: boolean) => void;
  setType: (data: string) => void;
  setMessage: (data: string) => void;
  responseHandler: (response: AxiosResponse<any, any>) => void;
}

const processRequest = (request: RequestContext): void => {
  request.prefetch?.();
  request.setLoading(true);
  request
    .request(request.inputData)
    .then(response => {
      if (response.status === 200) {
        request.responseHandler(response);
      } else {
        request.setType(NOTIFICATION_TYPES.FAILED);
        request.setMessage(String(response));
      }
    })
    .catch(error => {
      request.setType(NOTIFICATION_TYPES.FAILED);
      if (error?.response?.data?.message !== undefined) {
        request.setMessage(error.response.data.message);
      } else {
        request.setMessage(error.message);
      }
    })
    .finally(() => {
      request.setLoading(false);
      request.postfetch?.();
    });
};

// ======= MS GRAPH APIS ============= //
export const getProfilePicture = async (): Promise<AxiosResponse<any, Blob>> => {
  try {
    return await httpService.httpMsGraph.get('me/photo/$value', {
      headers: {
        'Content-Type': 'image/jpeg'
      },
      responseType: 'blob'
    });
  } catch (e) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return await Promise.resolve({ data: null } as AxiosResponse);
  }
};

const getMSGraphUsersCountry = async (): Promise<string | null> => {
  try {
    return await httpService.httpMsGraph.get<string>('me/country/$value').then(response => response.data);
  } catch (e) {
    return await Promise.resolve(null);
  }
};

export default {
  getApplicationBrandings,
  getApplicationBrandingTimestamp,
  getCountriesDscUm,
  getCountries,
  getLanguages,
  getCurrentCountry,
  activateUser,
  getBrandingObjects2,
  getRegion,
  processRequest,
  getRegionsDscUm,
  getProfilePicture,
  getMSGraphUsersCountry,
  getAgs,
};
